import { useState, useEffect } from 'react';
import { Modal, Badge, Spinner, Alert } from 'flowbite-react';
import { activityLogApi } from '../../api/activityLog';
import type { ActivityLogDetail } from '../../api/activityLog';
import { getUserErrorMessage } from '../../api';

interface ActivityDetailModalProps {
  activityLogId: number | null;
  show: boolean;
  onClose: () => void;
}

/** Helper row for key-value display */
const DetailRow: React.FC<{
  label: string;
  value?: string;
  children?: React.ReactNode;
}> = ({ label, value, children }) => (
  <div className="flex justify-between items-center">
    <span className="text-gray-600 dark:text-gray-400">{label}:</span>
    {children || <span className="font-medium text-gray-900 dark:text-white">{value}</span>}
  </div>
);

export const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({
  activityLogId,
  show,
  onClose,
}) => {
  const [activity, setActivity] = useState<ActivityLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && activityLogId) {
      fetchDetail();
    }
    if (!show) {
      setActivity(null);
      setError(null);
    }
  }, [show, activityLogId]);

  const fetchDetail = async () => {
    if (!activityLogId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await activityLogApi.getDetail(activityLogId);
      setActivity(data);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to load activity details'));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Modal show={show} onClose={onClose} size="2xl" dismissible popup>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Activity Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : activity ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Basic Information */}
            <section>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Basic Information
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2 text-sm">
                <DetailRow label="ID" value={`#${activity.activityLogId}`} />
                <DetailRow label="Timestamp" value={formatTimestamp(activity.createdAt)} />
                <DetailRow label="Category">
                  <Badge color="info">{activity.eventCategory}</Badge>
                </DetailRow>
                <DetailRow label="Event Type">
                  <span className="font-mono text-sm text-gray-900 dark:text-white">{activity.eventType}</span>
                </DetailRow>
              </div>
            </section>

            {/* Entity Information */}
            <section>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Entity Information
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2 text-sm">
                <DetailRow label="Entity Type" value={activity.entityType} />
                <DetailRow label="Entity ID" value={`#${activity.entityId}`} />
                {activity.educationOrganizationName && (
                  <DetailRow label="Organization" value={activity.educationOrganizationName} />
                )}
              </div>
            </section>

            {/* Actor Information */}
            <section>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Actor Information
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2 text-sm">
                <DetailRow
                  label="Actor"
                  value={activity.actorName || `User #${activity.actorId}`}
                />
                <DetailRow label="Actor Type">
                  <Badge color="gray">{activity.actorType}</Badge>
                </DetailRow>
                {activity.ipAddress && (
                  <DetailRow label="IP Address">
                    <span className="font-mono text-sm text-gray-900 dark:text-white">{activity.ipAddress}</span>
                  </DetailRow>
                )}
              </div>
            </section>

            {/* Action Description */}
            <section>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                Description
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-sm text-gray-900 dark:text-white">
                  {activity.actionDescription}
                </p>
              </div>
            </section>

            {/* Event Data (JSON) */}
            {activity.eventData && Object.keys(activity.eventData).length > 0 && (
              <section>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Event Data
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <pre className="text-xs overflow-auto text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {JSON.stringify(activity.eventData, null, 2)}
                  </pre>
                </div>
              </section>
            )}

            {/* Related Entities */}
            {activity.relatedEntities && Object.keys(activity.relatedEntities).length > 0 && (
              <section>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Related Entities
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(activity.relatedEntities).map(([key, value]) => (
                      <Badge key={key} color="gray">
                        {key}: {JSON.stringify(value)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Technical Details */}
            {activity.userAgent && (
              <section>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Technical Details
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 break-all">
                    {activity.userAgent}
                  </p>
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              No activity details available.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
